import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { cn, IconUrgent } from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

export interface TaskPriorityIconProps {
  priority: StandardTask['priority']
  isTaskCompletedOrCancelled?: boolean
  className?: string
  tooltip?: string
}

export function TaskPriorityIcon({
  priority,
  isTaskCompletedOrCancelled,
  className,
  tooltip,
}: TaskPriorityIconProps): JSX.Element {
  // SVG icons from linear.app
  const icon = match(priority)
    .with(undefined, () => (
      <svg
        aria-label="No Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="1.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
        <rect
          x="6.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
        <rect
          x="11.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
      </svg>
    ))
    .with('stat', () => (
      <IconUrgent
        className={cn(
          !isTaskCompletedOrCancelled && 'fill-orange-500',
          !!isTaskCompletedOrCancelled && 'fill-[lch(39.48%_1_282.863/1)]',
          className
        )}
      />
    ))
    .with('urgent', () => (
      <svg
        aria-label="High Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
        <rect x="11.5" y="2" width="3" height="12" rx="1"></rect>
      </svg>
    ))
    .with('asap', () => (
      <svg
        aria-label="Medium Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
        <rect
          x="11.5"
          y="2"
          width="3"
          height="12"
          rx="1"
          fillOpacity="0.4"
        ></rect>
      </svg>
    ))
    .with('routine', () => (
      <svg
        aria-label="Low Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect
          x="6.5"
          y="5"
          width="3"
          height="9"
          rx="1"
          fillOpacity="0.4"
        ></rect>
        <rect
          x="11.5"
          y="2"
          width="3"
          height="12"
          rx="1"
          fillOpacity="0.4"
        ></rect>
      </svg>
    ))
    .exhaustive()

  if (!tooltip) {
    return icon
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{icon}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
